import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useMenuDataSimple } from '../../../hooks/useMenuDataSimple';
import { getNavigationMenu } from '../../../utils/menuDataConverter';
import { policyApi, PolicyListParams, PolicyItem } from '../../../services/policyService';

interface PolicyPageProps {
  onViewDetail?: (item: any, type: string) => void;
}

const PolicyPage: React.FC<PolicyPageProps> = ({ onViewDetail }) => {
  const { menuData } = useMenuDataSimple();
  
  // 获取导航菜单并找到当前路径对应的图标
  const navIcon = useMemo(() => {
    const navMenu = getNavigationMenu(menuData);
    const currentMenuItem = navMenu.find((item: { path: string }) => item.path === '/show/policy' || item.path === 'show/policy');
    return currentMenuItem?.icon || '&#xe6a7;'; // 默认使用数据库中的图标
  }, [menuData]);
  const [selectedTab, setSelectedTab] = useState('policy_documents');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const policyTabs = [
    {
      key: 'policy_documents',
      label: '政策性文件'
    },
    {
      key: 'other_public_documents',
      label: '其他主动公开文件'
    },
    {
      key: 'policy_interpretation',
      label: '政策解读'
    }
  ];

  // 加载政策数据
  useEffect(() => {
    let isMounted = true;
    
    const loadPolicies = async () => {
      // 防止重复调用
      if (loadingRef.current) return;
      loadingRef.current = true;
      
      try {
        setLoading(true);
        const params: PolicyListParams = {
          current: currentPage,
          size: pageSize,
          businessCategory: selectedTab
        };

        const response = await policyApi.getList(params);
        if (isMounted) {
          // 使用 requestAnimationFrame 延迟状态更新，避免在页面加载时触发布局调整
          requestAnimationFrame(() => {
            setPolicies(response.data.records || []);
            setTotal(response.data.total || 0);
            setLoading(false);
            loadingRef.current = false;
          });
        }
      } catch (error) {
        console.error('加载政策数据失败:', error);
        if (isMounted) {
          requestAnimationFrame(() => {
            setPolicies([]);
            setTotal(0);
            setLoading(false);
            loadingRef.current = false;
          });
        }
      }
    };

    loadPolicies();
    
    return () => {
      isMounted = false;
    };
  }, [selectedTab, currentPage]);

  const handleTabChange = (tabKey: string) => {
    setSelectedTab(tabKey);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // 格式化为 yyyy-mm-dd
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTabLabel = (tabKey: string) => {
    const tab = policyTabs.find(t => t.key === tabKey);
    return tab ? tab.label : '';
  };

  return (
    <div className="policy-page">
      <div className="policy-layout">
        {/* 左侧功能列表 */}
        <div className="policy-sidebar">
          <div className="sidebar-container">
            <div className="sidebar-header">
              <span className="icon-text">
                <i 
                  className="iconfont" 
                  dangerouslySetInnerHTML={{ __html: navIcon }}
                ></i>
                <span>政策导航</span>
              </span>
              <div className="header-arrow">▼</div>
            </div>
            <div className="sidebar-menu">
              {policyTabs.map(tab => (
                <div
                  key={tab.key}
                  className={`menu-item ${selectedTab === tab.key ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.key)}
                >
                  <span className="icon-text">
                    <i className="iconfont icon-quanlingyuguimo"></i>
                    <span>{tab.label}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 右侧内容区 */}
        <div className="policy-main">
          <div className="policy-content-section">
            <div className="policy-header">
              <h1 className="policy-header-title">
                {getTabLabel(selectedTab)}
              </h1>
              <div className="policy-header-divider"></div>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-text">加载中...</div>
              </div>
            ) : (
              <div className="policy-list-section">
                {policies.length > 0 ? (
                  <>
                    <div className="policy-list">
                      {policies.map((item) => (
                        <div
                          key={item.id}
                          className="policy-item"
                        >
                          <div className="policy-title">
                            {item.webLink && item.webLink !== '未公示' && item.webLink.trim() !== '' ? (
                              <a
                                href={item.webLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="policy-filename-link"
                                title={item.fileName}
                              >
                                {item.fileName}
                              </a>
                            ) : (
                              <span className="policy-filename-text" title={item.fileName}>
                                {item.fileName}
                              </span>
                            )}
                          </div>
                          <div className="policy-date">{formatDate(item.issuanceDate)}</div>
                        </div>
                      ))}
                    </div>

                    {/* 分页组件 - 始终显示 */}
                    {total > 0 && (
                      <div className="pagination-container">
                        <div className="pagination-info">
                          共 {total} 条记录，第 {currentPage} / {totalPages} 页
                        </div>
                        <div className="pagination-buttons">
                          <button
                            className="pagination-btn"
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                          >
                            上一页
                          </button>
                          <div className="pagination-numbers">
                            {(() => {
                              const pages = [];
                              const maxVisiblePages = 7; // 最多显示7个页码

                              if (totalPages <= maxVisiblePages) {
                                // 总页数不超过7页，显示所有页码
                                for (let i = 1; i <= totalPages; i++) {
                                  pages.push(
                                    <button
                                      key={i}
                                      className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  );
                                }
                              } else {
                                // 总页数超过7页，使用智能分页
                                const startPage = Math.max(1, currentPage - 3);
                                const endPage = Math.min(totalPages, currentPage + 3);

                                // 第一页
                                if (startPage > 1) {
                                  pages.push(
                                    <button
                                      key={1}
                                      className="pagination-number"
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  );
                                  if (startPage > 2) {
                                    pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
                                  }
                                }

                                // 中间页码
                                for (let i = startPage; i <= endPage; i++) {
                                  pages.push(
                                    <button
                                      key={i}
                                      className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  );
                                }

                                // 最后一页
                                if (endPage < totalPages) {
                                  if (endPage < totalPages - 1) {
                                    pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
                                  }
                                  pages.push(
                                    <button
                                      key={totalPages}
                                      className="pagination-number"
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  );
                                }
                              }

                              return pages;
                            })()}
                          </div>
                          <button
                            className="pagination-btn"
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            下一页
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-policy">暂无数据</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyPage;
